import type { ComplaintItemViewModel } from "../ViewModel";
import getComplaintTypes from "../getComplaintTypes";
import { formatDecimalNumber } from "../../../Common/DetailsForm/Presenter/Helpers/formatDecimalNumber";
import { limitedText } from "../../../Common/DetailsForm/Presenter/Helpers/limitedText";
import { useEffect, useState } from "react";

export default function ComplaintItemDisplayFragment(props: {
  items: ComplaintItemViewModel[];
  onRemoveItem: (index: number) => void;
  onEditItem: (index: number) => void;
  onNewItem: () => void;
  isNewItemEnabled: boolean;
  onOpenDocumentSelector: () => void;
}) {
  const [complaintTypeIdToNameMap, setComplaintTypeIdToNameMap] = useState<
    Map<number, string>
  >(new Map<number, string>());

  useEffect(() => {
    let ignore = false; // ignore gets true when the component unmounts, so we can prevent setting state on an unmounted component after an async operation finishes

    async function onInitialize() {
      const retrievedComplaintTypes = await getComplaintTypes();
      // we don't update the state if the component unmounted while we were waiting for the response, to avoid errors
      if (!ignore) {
        setComplaintTypeIdToNameMap(
          new Map(retrievedComplaintTypes.map((x) => [x.id, x.name])),
        );
      }
    }

    onInitialize();

    return () => {
      ignore = true;
    };
  }, []);

  return (
    <>
      {props.items.length > 0 && (
        <table className="table table-striped" style={{backgroundColor: "rgba(248, 249, 250, 0.8)"}}>
          <thead>
            <tr>
              <th>Poz</th>
              <th>Kod pozycji</th>
              <th>Nazwa towaru</th>
              <th>Numer katalogowy</th>
              <th className="text-center">Ilość do reklamacji</th>
              <th>Rodzaj reklamacji</th>
              <th>Uwagi</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {props.items.map((item: ComplaintItemViewModel, index) => (
              <tr key={item.id}>
                <td className="text-end">{index + 1}</td>
                <td>
                  {item.documentItem?.lineCode}
                </td>
                <td>
                  {item.documentItem?.productName}
                </td>
                <td>
                  {item.documentItem?.catalogNumber}
                </td>
                <td className="text-end">
                  {formatDecimalNumber(
                    // biome-ignore lint/style/noNonNullAssertion: at this point the amount should have been filled in, so it's safe to use non-null assertion here.
                    item.amount!,4)}
                </td>
                <td>
                  {complaintTypeIdToNameMap.get(
                    // biome-ignore lint/style/noNonNullAssertion: at this point the amount should have been filled in, so it's safe to use non-null assertion here.
                    item.complaintTypeId!,
                  ) ?? ""}
                </td>
                <td>
                  {limitedText(
                    item.comments || "",
                    30,
                  )}
                </td>
                <td>
                  {/** biome-ignore lint/a11y/useValidAnchor: button link causes layout problems */}
                  {/** biome-ignore lint/a11y/useSemanticElements: button link causes layout problems */}
                  <a
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      props.onRemoveItem(index);
                    }}
                    href="#"
                    role="button"
                    className="link-danger me-3 small text-decoration-none"
                    title="Kliknij, aby usunąć tą pozycję z formularza."
                  >
                    <i className="bi bi-trash3 ps-2"></i> Usuń
                  </a>
                  {/** biome-ignore lint/a11y/useValidAnchor: button link causes layout problems */}
                  {/** biome-ignore lint/a11y/useSemanticElements: button link causes layout problems  */}
                  <a
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      props.onEditItem(index);
                    }}
                    href="#"
                    role="button"
                    className="link-primary small text-decoration-none"
                    title="Kliknij, aby zmienić zawartość tej pozycji."
                  >
                    <i className="bi bi-pencil-square"></i> Edytuj
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {props.items.length === 0 && (
        <div className="row mb-3">
          <div className="col-sm-12">
            <div
              className="alert alert-primary d-flex align-items-center"
              role="alert"
            >
              <div className="flex-shrink-0 ms-3 me-4">
                <i className="bi bi-lightbulb"></i>
              </div>
              <div>
                <b>Brak pozycji</b>
                <br />
                Przed wysłaniem zgłoszenia musisz dodać co najmniej jedną
                pozycję, której dotyczy zgłoszenie.
                {!props.isNewItemEnabled && (
                  <>
                    <br />
                    <b>Dokument nie został wybrany</b>
                    <br />
                    Aby dodać pozycję, musisz najpierw{" "}
                    {/** biome-ignore lint/a11y/useValidAnchor: button link causes layout problems */}
                    {/** biome-ignore lint/a11y/useSemanticElements: button link causes layout problems */}
                    <a
                      role="button"
                      href="#"
                      onClick={(
                        e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
                      ) => {
                        e.preventDefault();
                        props.onOpenDocumentSelector();
                      }}
                      className="alert-link"
                    >
                      <b>wybrać dokument sprzedaży</b>.
                    </a>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="row mb-3">
        <div className="col-sm-12">
          <button
            type="button"
            onClick={() => {
              props.onNewItem();
            }}
            className={`btn btn-success py-1 ps-4 pe-5`}
            disabled={!props.isNewItemEnabled}
          >
            <i className="bi bi-plus-circle ps-2 me-2"></i> Dodaj pozycję
          </button>
        </div>
      </div>
    </>
  );
}
